import React, { useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Stack,
  Chip,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  FilterList,
  Download,
  Refresh,
  Block,
  CheckCircle,
  Message,
  Wallpaper,
  RestartAlt,
  SystemUpdate,
  Translate,
  MoreVert,
  MoreHoriz,
  GridView,
  SignalWifiOff,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import StatusBadge from './shared/StatusBadge';
import SectionHeader from './shared/SectionHeader';
import EmptyState from './shared/EmptyState';
import { tokens } from '../theme';

// ─── Tiempo relativo ────────────────────────────────────────────────────────
const formatRelativeTime = (dateString) => {
  const ms    = Date.now() - new Date(dateString);
  const mins  = Math.floor(ms / 60000);
  const hours = Math.floor(ms / 3600000);
  const days  = Math.floor(ms / 86400000);
  if (mins < 1)   return 'Ahora';
  if (mins < 60)  return `${mins} min`;
  if (hours < 24) return `${hours} h`;
  if (days < 7)   return `${days} días`;
  return new Date(dateString).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
};

const EnhancedDeviceTable = ({ devices = [], loading = false, onRefresh, onAction }) => {
  const [search,   setSearch]   = useState('');
  const [statusF,  setStatusF]  = useState([]);
  const [mfrF,     setMfrF]     = useState([]);
  // Selección no controlada: DataGrid v8 gestiona su propio estado interno.
  // Capturamos los IDs seleccionados solo en el callback para evitar el crash
  // de gridRowSelectionSelector cuando se pasa rowSelectionModel como prop controlado.
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkMenu, setBulkMenu] = useState(null);
  const [filterMenu, setFilterMenu] = useState(null);
  const [rowMenuAnchor, setRowMenuAnchor] = useState(null);
  const [rowMenuDevice, setRowMenuDevice] = useState(null);

  const manufacturers = useMemo(
    () => [...new Set(devices.map((d) => d.manufacturer).filter(Boolean))],
    [devices]
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return devices.filter((d) => {
      const matchSearch =
        !q ||
        d.device_name?.toLowerCase().includes(q) ||
        d.device_id?.toLowerCase().includes(q) ||
        d.manufacturer?.toLowerCase().includes(q) ||
        d.model?.toLowerCase().includes(q);
      const matchStatus = statusF.length === 0 || statusF.includes(d.status);
      const matchMfr    = mfrF.length === 0    || mfrF.includes(d.manufacturer);
      return matchSearch && matchStatus && matchMfr;
    });
  }, [devices, search, statusF, mfrF]);

  const activeFilterCount = statusF.length + mfrF.length;

  const handleExport = () => {
    const rows = [
      ['Nombre', 'ID', 'Estado', 'Fabricante', 'Modelo', 'Android', 'Versión', 'Última conexión'],
      ...filtered.map((d) => [
        d.device_name || '',
        d.device_id,
        d.status,
        d.manufacturer,
        d.model,
        d.android_version,
        d.launcher_version,
        new Date(d.last_seen).toLocaleString('es-ES'),
      ]),
    ].map((r) => r.join(',')).join('\n');

    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([rows], { type: 'text/csv' }));
    a.download = `dispositivos_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const handleBulkAction = (action) => {
    setBulkMenu(null);
    if (!selectedIds.length) return;
    const selectedSet = new Set(selectedIds.map(String));
    const selectedDevices = devices.filter((d) => selectedSet.has(String(d.id)));
    onAction?.(action, selectedDevices);
  };

  const openRowMenu = (event, row) => {
    event.stopPropagation();
    setRowMenuAnchor(event.currentTarget);
    setRowMenuDevice(row);
  };

  const closeRowMenu = () => {
    setRowMenuAnchor(null);
    setRowMenuDevice(null);
  };

  const handleRowAction = (action) => {
    if (!rowMenuDevice) return;
    const selectedRow = rowMenuDevice;
    closeRowMenu();
    onAction?.(action, [selectedRow]);
  };

  const columns = [
    {
      field: 'device_name',
      headerName: 'Dispositivo',
      width: 220,
      flex: 1,
      renderCell: ({ row }) => (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.ink.primary, lineHeight: 1.3 }}>
            {row.device_name || 'Sin asignar'}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: tokens.color.ink.muted,
              fontFamily: tokens.font.mono,
              fontSize: '0.7rem',
              display: 'block',
              mt: 0.25,
            }}
          >
            {row.device_id}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 115,
      renderCell: ({ value }) => <StatusBadge status={value} />,
    },
    {
      field: 'manufacturer',
      headerName: 'Dispositivo',
      width: 150,
      renderCell: ({ row }) => (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
            {row.manufacturer}
          </Typography>
          <Typography variant="caption" sx={{ color: tokens.color.ink.muted, display: 'block' }}>
            {row.model}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'android_version',
      headerName: 'Android',
      width: 90,
      renderCell: ({ value }) => (
        <Chip
          label={value}
          size="small"
          sx={{
            fontFamily: tokens.font.mono,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: tokens.color.brand[50],
            color: tokens.color.brand[600],
            border: `1px solid ${tokens.color.brand[100]}`,
          }}
        />
      ),
    },
    {
      field: 'launcher_version',
      headerName: 'App',
      width: 80,
      renderCell: ({ value }) => (
        <Typography
          variant="caption"
          sx={{ fontFamily: tokens.font.mono, color: tokens.color.ink.secondary, fontWeight: 500 }}
        >
          {value || '—'}
        </Typography>
      ),
    },
    {
      field: 'last_seen',
      headerName: 'Última señal',
      width: 120,
      renderCell: ({ value, row }) => {
        const isOnline = Date.now() - new Date(value) < 5 * 60 * 1000;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            {isOnline && (
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: tokens.color.status.active.text,
                  flexShrink: 0,
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                    '50%':      { opacity: 0.6, transform: 'scale(0.85)' },
                  },
                }}
              />
            )}
            <Typography
              variant="caption"
              sx={{
                color: isOnline ? tokens.color.status.active.text : tokens.color.ink.muted,
                fontWeight: isOnline ? 600 : 400,
              }}
            >
              {formatRelativeTime(value)}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: '__actions',
      headerName: 'Acciones',
      width: 280,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ py: 0.5 }}>
          <Button
            size="small"
            variant={row.status === 'active' ? 'outlined' : 'contained'}
            color={row.status === 'active' ? 'error' : 'success'}
            startIcon={row.status === 'active' ? <Block sx={{ fontSize: 14 }} /> : <CheckCircle sx={{ fontSize: 14 }} />}
            onClick={(e) => {
              e.stopPropagation();
              onAction?.(row.status === 'active' ? 'suspend' : 'activate', [row]);
            }}
            sx={{ minWidth: 92 }}
          >
            {row.status === 'active' ? 'Suspender' : 'Activar'}
          </Button>

          <Button
            size="small"
            variant="text"
            endIcon={<MoreHoriz sx={{ fontSize: 16 }} />}
            onClick={(e) => openRowMenu(e, row)}
            sx={{ minWidth: 110, color: tokens.color.ink.secondary }}
          >
            Mas acciones
          </Button>
        </Stack>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} thickness={3} sx={{ color: tokens.color.brand[500] }} />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <SectionHeader
          title="Dispositivos"
          subtitle={
            `${filtered.length}${filtered.length !== devices.length ? ` de ${devices.length}` : ''} dispositivos` +
            (selectedIds.length ? ` · ${selectedIds.length} seleccionados` : '')
          }
          actions={
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Download sx={{ fontSize: 16 }} />}
                onClick={handleExport}
                disabled={!filtered.length}
              >
                CSV
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Refresh sx={{ fontSize: 16 }} />}
                onClick={onRefresh}
              >
                Actualizar
              </Button>
              {selectedIds.length > 0 && (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    endIcon={<MoreVert sx={{ fontSize: 16 }} />}
                    onClick={(e) => setBulkMenu(e.currentTarget)}
                  >
                    Acciones ({selectedIds.length})
                  </Button>
                  <Menu
                    anchorEl={bulkMenu}
                    open={Boolean(bulkMenu)}
                    onClose={() => setBulkMenu(null)}
                    PaperProps={{ elevation: 2, sx: { minWidth: 180, mt: 0.5 } }}
                  >
                    <MenuItem onClick={() => handleBulkAction('message')}>
                      <Message sx={{ mr: 1.5, fontSize: 18 }} /> Enviar mensaje
                    </MenuItem>
                    <MenuItem onClick={() => handleBulkAction('wallpaper')}>
                      <Wallpaper sx={{ mr: 1.5, fontSize: 18 }} /> Cambiar fondo
                    </MenuItem>
                    <MenuItem onClick={() => handleBulkAction('set_language')}>
                      <Translate sx={{ mr: 1.5, fontSize: 18 }} /> Cambiar idioma
                    </MenuItem>
                    <MenuItem onClick={() => handleBulkAction('reboot')}>
                      <RestartAlt sx={{ mr: 1.5, fontSize: 18 }} /> Reiniciar
                    </MenuItem>
                    <MenuItem onClick={() => handleBulkAction('update_app')}>
                      <SystemUpdate sx={{ mr: 1.5, fontSize: 18 }} /> Actualizar app
                    </MenuItem>
                    <MenuItem onClick={() => handleBulkAction('suspend')} sx={{ color: tokens.color.semantic.error }}>
                      <Block sx={{ mr: 1.5, fontSize: 18 }} /> Suspender
                    </MenuItem>
                  </Menu>
                </>
              )}
            </>
          }
        />

        {/* Barra de búsqueda + filtros */}
        <Stack direction="row" spacing={1.5} sx={{ mb: 2.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar por nombre, ID, fabricante, modelo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant={activeFilterCount > 0 ? 'contained' : 'outlined'}
            size="small"
            startIcon={<FilterList sx={{ fontSize: 16 }} />}
            onClick={(e) => setFilterMenu(e.currentTarget)}
            sx={{ flexShrink: 0, minWidth: 110 }}
          >
            Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </Button>

          <Menu
            anchorEl={filterMenu}
            open={Boolean(filterMenu)}
            onClose={() => setFilterMenu(null)}
            PaperProps={{ elevation: 2, sx: { p: 2, minWidth: 240, mt: 0.5 } }}
          >
            <Typography variant="overline" sx={{ mb: 1.5, display: 'block' }}>
              Filtrar por
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Estado</InputLabel>
              <Select
                multiple
                size="small"
                value={statusF}
                onChange={(e) => {
                  const { value } = e.target;
                  setStatusF(typeof value === 'string' ? value.split(',') : value);
                }}
                renderValue={(sel) => sel.map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}
              >
                {['active', 'suspended', 'offline'].map((s) => (
                  <MenuItem key={s} value={s}>
                    <Checkbox checked={statusF.includes(s)} size="small" />
                    <ListItemText primary={s === 'active' ? 'Activo' : s === 'suspended' ? 'Suspendido' : 'Offline'} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {manufacturers.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Fabricante</InputLabel>
                <Select
                  multiple
                  size="small"
                  value={mfrF}
                  onChange={(e) => {
                    const { value } = e.target;
                    setMfrF(typeof value === 'string' ? value.split(',') : value);
                  }}
                  renderValue={(sel) => `${sel.length} seleccionados`}
                >
                  {manufacturers.map((m) => (
                    <MenuItem key={m} value={m}>
                      <Checkbox checked={mfrF.includes(m)} size="small" />
                      <ListItemText primary={m} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Button
              fullWidth
              size="small"
              variant="text"
              onClick={() => { setStatusF([]); setMfrF([]); }}
              disabled={!activeFilterCount}
              sx={{ color: tokens.color.semantic.error }}
            >
              Limpiar filtros
            </Button>
          </Menu>
        </Stack>

        <Menu
          anchorEl={rowMenuAnchor}
          open={Boolean(rowMenuAnchor)}
          onClose={closeRowMenu}
          PaperProps={{ elevation: 2, sx: { minWidth: 210, mt: 0.5 } }}
        >
          <MenuItem onClick={() => handleRowAction('message')}>
            <Message sx={{ mr: 1.5, fontSize: 18 }} /> Enviar mensaje
          </MenuItem>
          <MenuItem onClick={() => handleRowAction('wallpaper')}>
            <Wallpaper sx={{ mr: 1.5, fontSize: 18 }} /> Cambiar fondo
          </MenuItem>
          <MenuItem onClick={() => handleRowAction('reboot')}>
            <RestartAlt sx={{ mr: 1.5, fontSize: 18 }} /> Reiniciar dispositivo
          </MenuItem>
          <MenuItem onClick={() => handleRowAction('update_app')}>
            <SystemUpdate sx={{ mr: 1.5, fontSize: 18 }} /> Actualizar aplicacion
          </MenuItem>
          <MenuItem onClick={() => handleRowAction('home_shortcut')}>
            <GridView sx={{ mr: 1.5, fontSize: 18 }} /> Configurar accesos
          </MenuItem>
          <MenuItem onClick={() => handleRowAction('set_language')}>
            <Translate sx={{ mr: 1.5, fontSize: 18 }} /> Cambiar idioma
          </MenuItem>
        </Menu>

        {/* Tabla */}
        {filtered.length === 0 ? (
          <EmptyState
            icon={<SignalWifiOff />}
            title="Sin resultados"
            description={
              search || activeFilterCount
                ? 'Ningún dispositivo coincide con los filtros actuales.'
                : 'No hay dispositivos registrados en el sistema.'
            }
            action={
              (search || activeFilterCount) ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => { setSearch(''); setStatusF([]); setMfrF([]); }}
                >
                  Limpiar filtros
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Box sx={{ height: 560, width: '100%' }}>
            <DataGrid
              rows={filtered}
              columns={columns}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              pageSizeOptions={[10, 25, 50]}
              checkboxSelection
              disableRowSelectionOnClick
              onRowSelectionModelChange={(model) => {
                // model es Set<GridRowId> en v8 — convertimos a array para uso interno
                setSelectedIds([...model].map(String));
              }}
              rowHeight={56}
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: tokens.color.surface.subtle,
                  borderBottom: `1px solid ${tokens.color.border.default}`,
                  borderRadius: `${tokens.radius.md} ${tokens.radius.md} 0 0`,
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: `1px solid ${tokens.color.border.default}`,
                  outline: 'none !important',
                },
                '& .MuiDataGrid-row.Mui-selected': {
                  backgroundColor: `${tokens.color.brand[50]} !important`,
                  '&:hover': { backgroundColor: `${tokens.color.brand[100]} !important` },
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: `1px solid ${tokens.color.border.default}`,
                  backgroundColor: tokens.color.surface.subtle,
                },
                '& .MuiCheckbox-root.Mui-checked': {
                  color: tokens.color.brand[500],
                },
                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
              }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default EnhancedDeviceTable;
